import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useApprovePasswordStore,
  useCheckLimitStore,
  useExternalStore,
  useGetAccountsStore,
  useGetDictionariesStore,
  useNotificationStore,
} from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType } from '@shared/api/rest/models/enums';
import type { GetRequisitesResponse } from '@shared/api/rest/payments';
import { ButtonV2, Constants, ErrorMessage, Input, ItemsType, Modal, Select, Textarea } from '@shared/widgets';

interface IForm {
  debitAccount?: ItemsType;
  amount?: string;
  currency?: ItemsType;
  paymentReference: string;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, amount: '', currency: undefined, paymentReference: '' };

interface ExternalPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecipient?: GetRequisitesResponse;
  setIsOpenSelectAccountModal: (value: boolean) => void;
}

export const ExternalPaymentsModal = (props: ExternalPaymentsModalProps) => {
  const accountStore = useGetAccountsStore();
  const dictionariesStore = useGetDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const externalStore = useExternalStore();
  const approvePasswordStore = useApprovePasswordStore();
  const notificationStore = useNotificationStore();

  const [isContinue, setIsContinue] = useState<boolean>(false);
  const [password, setPassword] = useState<string>();

  const { formState, control, handleSubmit, watch, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const amount = watch('amount');
  const currency = watch('currency');
  const paymentReference = watch('paymentReference');

  const makeSelectCurrency = dictionariesStore.data?.Currency?.map((item) => ({ value: item.Id, label: item.Name, leftLabel: item.Name })) || [];
  const makeSelectAccount =
    accountStore.data?.map((item) => ({ value: item.Id, label: item.Currency && item.Amount, leftLabel: item.Currency })) || [];

  const submitHandler = async (data: IForm) => {
    const result = await checkLimitStore.checkLimit({
      accountIdFrom: data.debitAccount?.value,
      accountIdTo: props.selectedRecipient?.Id,
      amount: +data.amount!,
      currency: data.currency?.value,
      requestType: RequestType.TransferOut,
    });
    if (result.data?.LimitExceeded) {
      notificationStore.setError(result.data.Note);
      return;
    }

    await externalStore.post({
      AccountId: data.debitAccount?.value,
      Amount: +data.amount!,
      Currency: data.currency?.value,
      Reference: data.paymentReference,
      RequisitesId: props.selectedRecipient?.Id,
    });
    setIsContinue(true);
  };

  const handleOpenSelectModal = () => {
    props.setIsOpenSelectAccountModal(true);
    props.onClose();
  };

  const handleBackButtonClick = () => {
    setIsContinue(false);
    setPassword('');
  };

  const confirmModal = async () => {
    await approvePasswordStore.approvePassword({ Password: password!, RequestId: externalStore.data?.Id });
    notificationStore.setSuccess(Constants.DEFAULT_SUCCESS_MESSAGE);
    props.onClose();
    setIsContinue(false);
    reset(DEFAULT_VALUES);
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} title='External payments'>
      {!isContinue ? (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              defaultValue={makeSelectAccount[0]}
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select wrapperClassName='!p-0' items={makeSelectAccount} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <div className='mt-3'>
              <div className='text-xs font-semibold text-[#929292] uppercase'>Credit Accounts</div>
              <div className='flex h-[46px] cursor-pointer items-center justify-between border-b border-b-[#46413a]' onClick={handleOpenSelectModal}>
                <div className='text-sm font-semibold text-[#929292] uppercase'>
                  {props.selectedRecipient?.OwnerName || 'Please select or add new recipient'}
                </div>
                <div>
                  <svg width='7' height='12' viewBox='0 0 7 12' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M6.654 6.59l-5.096 5.096a.58.58 0 0 1-.82 0l-.411-.41a.58.58 0 0 1 0-.822L4.6 6.18.327 1.906a.58.58 0 0 1 0-.821l.41-.41a.58.58 0 0 1 .821 0L6.654 5.77a.58.58 0 0 1 0 .82z'
                      fill='#929292'
                      fillRule='evenodd'
                    />
                  </svg>
                </div>
              </div>
            </div>
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'Please enter Amount', maxLength: 100 }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    className='!h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    required
                    value={value}
                    onChange={(value) => AmountUtils.amountChange(value, onChange)}
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            <Controller
              name='currency'
              control={control}
              defaultValue={makeSelectCurrency?.[0]}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                  <Select wrapperClassName='!p-0' items={makeSelectCurrency} value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='paymentReference'
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Payment Reference</div>
                  <Textarea variant='outlined' rounded placeholder='Enter reference' value={value} onChange={onChange} />
                </div>
              )}
            />
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <ButtonV2 className='!h-[46px]' color='outlined' fullWidth onClick={props.onClose}>
              Cancel
            </ButtonV2>
            <ButtonV2 className='!h-[46px]' fullWidth type='submit' disabled={!props.selectedRecipient || !amount || formState.isSubmitting}>
              Apply
            </ButtonV2>
          </div>
        </form>
      ) : (
        <div className='w-full'>
          <div className='mb-4'>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Debit Account:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{debitAccount?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(debitAccount?.label as number)}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>To:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5 whitespace-nowrap'>{props.selectedRecipient?.OwnerName}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Amount:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{currency?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(+amount!)}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Payment Reference:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5 whitespace-nowrap'>{paymentReference}</div>
                </div>
              </div>
            </div>
          </div>
          <div className='mt-3'>
            <div className='mb-1 text-sm font-semibold text-[#666] uppercase'>To confirm Top Up please enter your password</div>
            <Input
              variant='outlined'
              rounded
              className='h-[48px]'
              placeholder='Password'
              name='Password'
              value={password}
              type='password'
              onChange={setPassword}
              required
            />
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <ButtonV2 fullWidth color='outlined' onClick={handleBackButtonClick}>
              Back
            </ButtonV2>
            <ButtonV2 fullWidth color='primary' disabled={password ? password.length < 1 : true} onClick={confirmModal}>
              Confirm
            </ButtonV2>
          </div>
        </div>
      )}
    </Modal>
  );
};
